import React, { useState, useEffect } from 'react';
import MPDashboardLayout from '../../components/mp/MPDashboardLayout';
import { dashboardApi, exercisesApi, postsApi, platformsApi } from '../../lib/mpApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Target, FileText, Globe, Users, Radio, TrendingUp, Clock, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const MPDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, postsData, exercisesData] = await Promise.all([
        dashboardApi.getStats().catch(() => null),
        postsApi.getAll().catch(() => []),
        exercisesApi.getAll().catch(() => []),
      ]);
      setStats(statsData);
      setRecentPosts(Array.isArray(postsData) ? postsData.slice(0, 5) : []);
      setExercises(Array.isArray(exercisesData) ? exercisesData : []);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const activeExercises = exercises.filter(e => e.status === 'enabled' || e.status === 'published');
  const totalPosts = stats?.total_posts || recentPosts.length;
  const totalExercises = stats?.total_exercises || exercises.length;
  const totalPlatforms = stats?.total_platforms || 0;
  const totalUsers = stats?.total_users || 0;

  const statCards = [
    { label: 'Total Exercises', value: totalExercises, icon: Target, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Total Posts', value: totalPosts, icon: FileText, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { label: 'Platforms', value: totalPlatforms, icon: Globe, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: 'Users', value: totalUsers, icon: Users, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  ];

  if (loading) {
    return (
      <MPDashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </MPDashboardLayout>
    );
  }

  return (
    <MPDashboardLayout title="Dashboard">
      <div className="space-y-6" data-testid="mp-dashboard">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <Card key={stat.label} className="border-border rounded-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-heading">{stat.label}</p>
                    <p className="text-2xl font-bold mt-1 font-heading">{stat.value}</p>
                  </div>
                  <div className={`w-10 h-10 rounded-sm ${stat.bg} flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Active Exercises & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Active Exercises */}
          <Card className="border-border rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2">
                <Radio className="w-4 h-4 text-red-500" /> Active Exercises
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeExercises.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No active exercises</p>
              ) : (
                <div className="space-y-3">
                  {activeExercises.map(ex => (
                    <div key={ex.exercise_id} className="flex items-center gap-3 p-3 border border-border rounded-sm">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{ex.name}</p>
                        <p className="text-xs text-muted-foreground">{ex.selected_platforms?.length || 0} platforms</p>
                      </div>
                      <Badge className={`rounded-sm text-[10px] ${
                        ex.status === 'enabled' ? 'status-enabled' : 'status-published'
                      }`}>{ex.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Posts */}
          <Card className="border-border rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2">
                <Clock className="w-4 h-4" /> Recent Posts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentPosts.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No posts yet</p>
              ) : (
                <div className="space-y-3">
                  {recentPosts.map(post => (
                    <div key={post.post_id} className="flex items-center gap-3 p-3 border border-border rounded-sm">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{post.title || post.content?.substring(0, 50)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-[10px] rounded-sm">{post.platform}</Badge>
                          <Badge className={`rounded-sm text-[10px] ${
                            post.status === 'published' ? 'status-published' :
                            post.status === 'draft' ? 'status-draft' : 'status-enabled'
                          }`}>{post.status}</Badge>
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground font-mono whitespace-nowrap">
                        {post.author?.name}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="border-border rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2">
              <Zap className="w-4 h-4" /> Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <a href="/mp/exercises" className="p-4 border border-border rounded-sm text-center hover:bg-accent transition" data-testid="mp-quick-exercises">
                <Target className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                <p className="text-xs font-medium">New Exercise</p>
              </a>
              <a href="/mp/social-posts" className="p-4 border border-border rounded-sm text-center hover:bg-accent transition" data-testid="mp-quick-social">
                <FileText className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
                <p className="text-xs font-medium">Create Post</p>
              </a>
              <a href="/mp/platforms" className="p-4 border border-border rounded-sm text-center hover:bg-accent transition" data-testid="mp-quick-platforms">
                <Globe className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                <p className="text-xs font-medium">Manage Platforms</p>
              </a>
              <a href="/mp/media" className="p-4 border border-border rounded-sm text-center hover:bg-accent transition" data-testid="mp-quick-media">
                <TrendingUp className="w-6 h-6 mx-auto mb-2 text-purple-500" />
                <p className="text-xs font-medium">Media Library</p>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </MPDashboardLayout>
  );
};

export default MPDashboardPage;
